import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import styled, { ThemeContext } from 'styled-components';
import { DesktopSeparator, ContentContainer } from '../../shared/sectionable/styles';
import ScrollTransition from '../../shared/scroll-transition';
import PrimaryContent from './primary-content';
import About from './about';
import Fund from './fund';
import LatestWires from './latest-wires';
import FeaturedWires from '../../shared/sectionable/featured-wires';
import Enquiry from './enquiry';
import EnquiryContext, { reducer, defaultState } from '../../shared/enquiry/context';
import StickyFooter from './sticky-footer';
import StickyHeader from './sticky-header';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  ${props => props.theme.isMobileOrTablet && `
    width: 100%;
  `};
  ${props => props.theme.isLarge && `
    width: 100%;
    padding: 0 32px;
  `};
  ${props => props.theme.isXLarge && `
    width: 953px;
    padding: 0;
  `};
  ${props => props.theme.isXXLarge && `
    width: 1200px;
    padding: 0;
  `};
`;

// eslint-disable-next-line complexity
export default function Contributor(props) {
  const { isLarge } = React.useContext(ThemeContext);
  const [yOffset, setYOffset] = React.useState(0);
  const [state, dispatch] = React.useReducer(reducer, defaultState);
  const {
    profile, isOfCurrentUser, investorTypes, viewAllWiresLink,
  } = props;
  const { company, funds } = profile;
  const viewDataAttributes = {
    'data-id': profile.id,
    'data-event-type': 'view',
    'data-event': 'full_profile_view',
    'data-statisticable': 'profile',
    'data-page': document.referrer,
  };
  const isPremiumFeaturedWiresEnabled = _.includes(profile.activeFeatures, 'premium_featured_wires');
  const isPremiumEnquiryFormEnabled = _.includes(profile.activeFeatures, 'premium_enquiry_form');
  const isPremiumWhiteLabelingEnabled = _.includes(profile.activeFeatures, 'premium_white_label');

  const stickyProps = {
    profile,
    followed: props.isProfileFollowedByCurrentUser,
    yOffset,
    isMyself: props.isOfCurrentUser,
    newNavEnabled: props.newNavEnabled,
  };

  const hideContributorInfo = profile.isCompanyProfile && profile.organization;

  return (
    <Container {...viewDataAttributes}>
      <ScrollTransition onScroll={setYOffset} />
      <EnquiryContext.Provider value={{ state, dispatch }}>
        {isLarge && <StickyHeader {...stickyProps} />}
        <PrimaryContent
          profile={profile}
          stats={props.stats}
          followed={props.isProfileFollowedByCurrentUser}
          isMyself={props.isOfCurrentUser}
          showBranding={isPremiumWhiteLabelingEnabled && !!_.get(company, 'logo')}
        />
        <ContentContainer>
          {!!isPremiumFeaturedWiresEnabled && <FeaturedWires objectId={profile.id} type="profile" separator />}
          <LatestWires profile={profile} viewAllWiresLink={viewAllWiresLink} type="profile" />
          {funds && funds.length > 0 && !!profile.activePartnership && (
            <>
              <DesktopSeparator />
              <Fund company={company} funds={funds} />
            </>
          )}
          <DesktopSeparator />
          {!hideContributorInfo
            && <About profile={profile} />}
          {!isOfCurrentUser && isPremiumEnquiryFormEnabled && profile.activePartnership && (
            <Enquiry profile={profile} investorTypes={investorTypes} />
          )}
        </ContentContainer>
        {!!(!isLarge && !props.isOfCurrentUser) && <StickyFooter {...stickyProps} />}
      </EnquiryContext.Provider>
    </Container>
  );
}

Contributor.propTypes = {
  profile: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    slug: PropTypes.string,
    organization: PropTypes.string,
    position: PropTypes.string,
    isCompanyProfile: PropTypes.bool,
    joinedCurrentRoleAt: PropTypes.string,
    recentCareer: PropTypes.shape({
      organisation: PropTypes.string,
      position: PropTypes.string,
      joinedAt: PropTypes.string,
      leftAt: PropTypes.string,
    }),
    joinedIndustryAt: PropTypes.string,
    accred: PropTypes.string,
    countryName: PropTypes.string,
    cityName: PropTypes.string,
    profilePictureUrl: PropTypes.string,
    headlineGrab: PropTypes.string,
    blurb: PropTypes.string,
    investmentPhilosophy: PropTypes.arrayOf(PropTypes.string),
    linkedinUrl: PropTypes.string,
    twitterUrl: PropTypes.string,
    activePartnership: PropTypes.shape({
      id: PropTypes.number,
    }),
    areasOfExpertise: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      slug: PropTypes.string,
      name: PropTypes.string,
    })),
    canPostWires: PropTypes.bool,
    company: PropTypes.shape({
      name: PropTypes.string,
      logo: PropTypes.string,
      color: PropTypes.string,
    }),
    funds: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
        publicUrl: PropTypes.string.isRequired,
      }),
    ),
    activeFeatures: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  stats: PropTypes.shape({
    viewsCount: PropTypes.number,
    followersCount: PropTypes.number,
    wiresCount: PropTypes.number,
    since: PropTypes.string,
  }).isRequired,
  isProfileFollowedByCurrentUser: PropTypes.bool.isRequired,
  isOfCurrentUser: PropTypes.bool.isRequired,
  investorTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
  ).isRequired,
  newNavEnabled: PropTypes.bool.isRequired,
  viewAllWiresLink: PropTypes.string.isRequired,
};
